import i18n from "@/i18n";

/**
 * Locale ativo do i18n, para o `Intl` formatar no idioma do usuário.
 *
 * Mesma escada do `lib/datetime.ts`, e de propósito: data e número respondem à
 * mesma pergunta, que é em que idioma esta tela está sendo lida.
 */
function activeLocale(): string {
  return i18n.language || "en-US";
}

interface CountOptions {
  /** Casas decimais fixas. Sem isto, o número sai inteiro. */
  decimals?: number;
}

/**
 * Contagem no idioma do usuário: `7.593` em pt-BR, `7,593` em en-US.
 *
 * O Diagnóstico formatava tudo com `toLocaleString("en-US")` fixo, em doze
 * pontos de chamada. Numa tela que vai ao cliente em português, `7,593` se lê
 * como sete vírgula cinco, e `R$ 91,177,776.45` é ilegível na primeira passada.
 *
 * Trocar o fixo por `pt-BR` seria o mesmo erro espelhado: o produto tem três
 * locales. Seguir o idioma ativo resolve os três sem caso especial.
 *
 * ⚠️ Não use para ausência. `—` é o token de "não sei" da tela, e quem chama
 * aqui está formatando um número que existe — inclusive o zero medido.
 */
export function formatCount(n: number, options: CountOptions = {}): string {
  const { decimals } = options;
  return n.toLocaleString(activeLocale(), {
    minimumFractionDigits: decimals ?? 0,
    maximumFractionDigits: decimals ?? 0,
  });
}

// Abreviação compacta de números grandes (tokens, valores monetários) para até 3 dígitos.
//
// Consolida as antigas funções duplicadas e aplica a checagem de estouro em todas as faixas 
// (ex: corta em 999.95k em vez de 1.000k para evitar "1000.0M" após o arredondamento).
//
// A verificação é aplicada recursivamente em laço sobre a escala de unidades (k, M, B, T).

/** Unidades da escada, da menor para a maior. Cada degrau é ×1.000. */
const UNITS = ["k", "M", "B", "T"] as const;

/** Ponto em que `toFixed(1)` arredondaria para quatro dígitos e a faixa estoura. */
const OVERFLOW_AT = 999.95;

/** Abrevia a partir de 1.000: `8.3M`, `999.9k`, `1.0B`. Dono único dos limiares. */
export function compactAbove1k(n: number): string {
  let value = n / 1_000;
  let unit = 0;
  while (value >= OVERFLOW_AT && unit < UNITS.length - 1) {
    value /= 1_000;
    unit += 1;
  }
  return `${value.toFixed(1)}${UNITS[unit]}`;
}

/**
 * Formata tokens para drilldown: exato abaixo de 1k, compacto acima.
 * Exibe o valor exato sub-1k para precisão em tabelas.
*/
export function detailedTokens(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n < 1_000) return String(n);
  return compactAbove1k(n);
}

/**
 * Formata custo em USD para exibição em cards/vitrine (máx. 7 chars).
 * Exibe centavos abaixo de $1k e abrevia acima de $1k para evitar truncamento de layout (PR #788).
 * O valor exato fica em `detailedUsd`, usado nas superfícies de drilldown.
 *
 * Custo não-zero que arredondaria para `$0.00` vira `<$0.01` — mesma política
 * do `<1k` de `compactTokens`, pelo mesmo motivo: abaixo do piso que a faixa
 * representa, dizer que é menor que o piso. Arredondar para zero um valor que
 * existe é a forma silenciosa do defeito que a #429 veio tirar da tela de
 * custo (achado do review: um projeto de runs baratas lia "gastou nada").
*/
export function compactUsd(n: number): string {
  if (!Number.isFinite(n)) return "$0.00";
  if (n >= 1_000) return `$${compactAbove1k(n)}`;
  const cents = n.toFixed(2);
  if (n > 0 && cents === "0.00") return "<$0.01";
  return `$${cents}`;
}

/** Mantém no mínimo os centavos ao podar zeros à direita: `0.003400` → `0.0034`,
 *  `0.001000` → `0.001`, e nunca `0.` nem `0`. */
const TRAILING_ZEROS = /(\.\d{2}\d*?)0+$/;

/**
 * Formata custo para drilldown: valor exato abaixo de um centavo, centavos
 * acima, e a mesma escada de abreviação de `compactUsd` acima de $1k.
 *
 * É para `compactUsd` o que `detailedTokens` é para `compactTokens` — a
 * metade que faltava na família. Onde a pergunta é "quanto exatamente este
 * agente consumiu", `<$0.01` não responde, e dois agentes de custo diferente
 * não podem virar a mesma célula: é o defeito que a PR #788 corrigiu do lado
 * dos tokens, e que voltou pelo lado do dinheiro quando a #429 aposentou o
 * formatador de seis casas do `CostSummaryTab`.
 *
 * Seis casas é o piso real do dado, não escolha estética: `estimate_cost`
 * grava `round(custo, 6)` e o resumo soma esses valores.
*/
export function detailedUsd(n: number): string {
  if (!Number.isFinite(n)) return "$0.00";
  if (n >= 1_000) return `$${compactAbove1k(n)}`;
  if (n > 0 && n < 0.01) return `$${n.toFixed(6).replace(TRAILING_ZEROS, "$1")}`;
  return `$${n.toFixed(2)}`;
}

export function compactTokens(n: number): string {
  if (!Number.isFinite(n)) return "0";
  if (n === 0) return "0";
  if (n < 1_000) return "<1k";
  return compactAbove1k(n);
}
